/**
 * useFinanzasPersonales — lee/guarda las finanzas personales (super-admin).
 * GET/POST /api/admin/finanzas-personales.
 *
 * Arquitectura DB-first (MercadoPago diario):
 * - Histórico de cobros MP/Stripe vive en Postgres (manual_transactions /
 *   stripe_purchases) vía webhooks — el panel NUNCA llama a la API de MP.
 * - El cliente NO hace polling: carga al montar, reusa cache, y solo vuelve a
 *   pedir datos en: (1) save local, (2) botón Actualizar/Sync (?fresh=1),
 *   (3) refetch si la data está stale al volver a la pestaña (>5 min).
 * - Cuando llega un webhook nuevo, el server invalida el feed; el next GET
 *   relee la DB (no MP HTTP).
 *
 * Contrato: body GET/POST = { meta, items, loan, history, transactions,
 * recurring, real? }. POST puede incluir success: true.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import type { FinanzasData } from "@/lib/finanzasFormat";

export const FINANZAS_QUERY_KEY = ["admin", "finanzas-personales"] as const;

/** Client cache: libro + feed DB son históricos; 5 min sin re-hitear admin API. */
const STALE_MS = 5 * 60_000;
const GC_MS = 30 * 60_000;

/** Normaliza la respuesta de la API a FinanzasData (arrays siempre definidos). */
export function normalizeFinanzasData(body: unknown): FinanzasData {
  const b = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;
  return {
    meta: (b.meta as FinanzasData["meta"]) ?? null,
    items: Array.isArray(b.items) ? (b.items as FinanzasData["items"]) : [],
    loan: Array.isArray(b.loan) ? (b.loan as FinanzasData["loan"]) : [],
    history: Array.isArray(b.history) ? (b.history as FinanzasData["history"]) : [],
    transactions: Array.isArray(b.transactions) ? (b.transactions as FinanzasData["transactions"]) : [],
    recurring: Array.isArray(b.recurring) ? (b.recurring as FinanzasData["recurring"]) : [],
    real: (b.real as FinanzasData["real"]) ?? null,
  };
}

function errorMessage(body: unknown, status: number): string {
  if (body && typeof body === "object") {
    const b = body as { error?: unknown; message?: unknown };
    const err = b.error != null ? String(b.error) : "";
    const msg = b.message != null ? String(b.message) : "";
    if (err && msg && msg !== err) return `${err}: ${msg}`;
    if (err) return err;
    if (msg) return msg;
  }
  return `HTTP ${status}`;
}

type AdminFetch = (
  input: string,
  init?: RequestInit & { headers?: HeadersInit },
) => Promise<Response>;

async function fetchFinanzas(
  adminFetch: AdminFetch,
  opts?: { fresh?: boolean },
): Promise<FinanzasData> {
  // fresh=1: relee Postgres (bypassa cache de feed en el server). No llama a MP API.
  const q = opts?.fresh ? "?fresh=1" : "";
  const res = await adminFetch(`/api/admin/finanzas-personales${q}`);
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) throw new Error(errorMessage(body, res.status));
  return normalizeFinanzasData(body);
}

export function useFinanzasPersonales(opts?: { enabled?: boolean }) {
  const { adminFetch, isLoaded: authLoaded } = useAdminAuth();
  // Caller must pass enabled: claimsLoaded && isSuperAdmin (avoids 403 spam).
  const enabled = authLoaded && (opts?.enabled ?? true);
  return useQuery({
    queryKey: FINANZAS_QUERY_KEY,
    enabled,
    // DB-first: no polling. Historical MP/Stripe already in Postgres.
    // Soft refetch only if data is stale when the window regains focus.
    staleTime: STALE_MS,
    gcTime: GC_MS,
    refetchInterval: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 1,
    queryFn: () => fetchFinanzas(adminFetch),
  });
}

/**
 * Relee el admin API y el feed desde Postgres (cache bust server).
 * Usar en “Actualizar” / “Sync MP” — NO es un pull a MercadoPago HTTP.
 */
export function useRefreshFinanzas() {
  const { adminFetch } = useAdminAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetchFinanzas(adminFetch, { fresh: true }),
    onSuccess: (data) => {
      queryClient.setQueryData(FINANZAS_QUERY_KEY, data);
    },
  });
}

export interface SaveFinanzasInput {
  table: "meta" | "items" | "loan" | "history" | "transactions" | "recurring";
  rows?: unknown[];
  deleteIds?: number[];
}

export function useSaveFinanzas() {
  const { adminFetch } = useAdminAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveFinanzasInput): Promise<FinanzasData> => {
      const res = await adminFetch("/api/admin/finanzas-personales", {
        method: "POST",
        body: JSON.stringify(input),
      });
      const body: unknown = await res.json().catch(() => null);
      if (!res.ok) throw new Error(errorMessage(body, res.status));
      return normalizeFinanzasData(body);
    },
    onMutate: async () => {
      // Cancel in-flight GETs so a slow poll cannot overwrite setQueryData after save.
      await queryClient.cancelQueries({ queryKey: FINANZAS_QUERY_KEY });
    },
    onSuccess: (data) => {
      // POST returns full state (libro + feed desde DB/cache) — no extra GET.
      queryClient.setQueryData(FINANZAS_QUERY_KEY, data);
    },
  });
}

export interface DolarOficial {
  venta: number | null;
  fecha: string | null;
}

/**
 * Cotización Dólar Oficial en vivo (dolarapi.com, API pública con CORS).
 * Sin auth. Se cachea 1h; si falla, el componente cae al override manual
 * (`meta.usd_ars_rate`). No bloquea el render del panel.
 */
export function useDolarOficial() {
  return useQuery({
    queryKey: ["cotizacion", "dolar-oficial"],
    staleTime: 60 * 60 * 1000,
    gcTime: 6 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    queryFn: async (): Promise<DolarOficial> => {
      const res = await fetch("https://dolarapi.com/v1/dolares/oficial", {
        headers: { accept: "application/json" },
      });
      if (!res.ok) throw new Error(`cotización HTTP ${res.status}`);
      const j = (await res.json()) as { venta?: unknown; fechaActualizacion?: unknown };
      const venta = Number(j?.venta);
      return {
        venta: Number.isFinite(venta) && venta > 0 ? venta : null,
        fecha: typeof j?.fechaActualizacion === "string" ? j.fechaActualizacion : null,
      };
    },
  });
}
